import { colors } from '@kmns-internal/furikake'
import { CheckOutlined } from '@mui/icons-material'
import {
  Box,
  ButtonBase,
  Drawer,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import type { IndicatedIssueLabel } from '@ulysses-inc/harami_api_client'
import type { FormikHelpers, FormikProps } from 'formik'
import { useFormik } from 'formik'
import type { FC } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import FormikErrorMessage from 'src/components/formikErrorMessage/FormikErrorMessage'
import { UUID } from 'src/exShared/util/uuid'
import Yup from 'src/features/validation/yup'
import indicatedIssuesOperations from 'src/state/ducks/indicatedIssues/operations'
import type { RootState } from 'src/state/store'
import FooterButton from 'src/views/components/drawer/FooterButton'

interface StateProps {
  indicatedIssueLabel?: IndicatedIssueLabel
  isShowDrawer: boolean
  isLoadingIndicatedIssueLabel: boolean
}

interface DispatchProps {
  addIndicatedIssueLabel: (indicatedIssueLabel: IndicatedIssueLabel) => void
  updateIndicatedIssueLabel: (indicatedIssueLabel: IndicatedIssueLabel) => void
  changeIsShowDrawer: (isShowDrawer: boolean) => void
}

interface MergeProps {
  addIndicatedIssueLabel: (indicatedIssueLabel: IndicatedIssueLabel) => void
  updateIndicatedIssueLabel: (indicatedIssueLabel: IndicatedIssueLabel) => void
}

type EditIndicatedIssueLabelProps = StateProps & DispatchProps & MergeProps

const useStateProps = (): StateProps => {
  return {
    indicatedIssueLabel: useSelector(
      (state: RootState) =>
        state.indicatedIssuesState.indicatedIssueLabel.indicatedIssueLabel,
      shallowEqual,
    ),
    isShowDrawer: useSelector(
      (state: RootState) =>
        state.indicatedIssuesState.indicatedIssueLabel.isShowDrawer,
      shallowEqual,
    ),
    isLoadingIndicatedIssueLabel: useSelector(
      (state: RootState) =>
        state.indicatedIssuesState.indicatedIssueLabel.isLoading,
      shallowEqual,
    ),
  }
}

const useDispatchProps = (): DispatchProps => {
  const dispatch = useDispatch()
  return {
    addIndicatedIssueLabel: (indicatedIssueLabel: IndicatedIssueLabel) => {
      indicatedIssuesOperations.addIndicatedIssueLabel(
        dispatch,
        indicatedIssueLabel,
      )
    },
    updateIndicatedIssueLabel: (indicatedIssueLabel: IndicatedIssueLabel) => {
      indicatedIssuesOperations.updateIndicatedIssueLabel(
        dispatch,
        indicatedIssueLabel,
      )
    },
    changeIsShowDrawer: (isShowDrawer: boolean) => {
      indicatedIssuesOperations.resetEditIndicatedIssueLabel(dispatch)
      indicatedIssuesOperations.changeIsShowDrawer(dispatch, isShowDrawer)
    },
  }
}

const useMergeProps = (
  _: StateProps,
  dispatchProps: DispatchProps,
): MergeProps => {
  return {
    addIndicatedIssueLabel: (indicatedIssueLabel: IndicatedIssueLabel) => {
      dispatchProps.addIndicatedIssueLabel(indicatedIssueLabel)
    },
    updateIndicatedIssueLabel: (indicatedIssueLabel: IndicatedIssueLabel) => {
      dispatchProps.updateIndicatedIssueLabel(indicatedIssueLabel)
    },
  }
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required().label('ラベル名'),
})

const EditIndicatedIssueLabelDrawerScene: FC<
  EditIndicatedIssueLabelProps & FormikProps<IndicatedIssueLabel>
> = (
  props: EditIndicatedIssueLabelProps & FormikProps<IndicatedIssueLabel>,
) => {
  const { handleSubmit, values, setFieldValue } = props

  return (
    <Drawer
      anchor="right"
      open={props.isShowDrawer}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') {
          return
        }
        props.changeIsShowDrawer(false)
      }}
      PaperProps={{
        sx: {
          width: 600,
          padding: 3,
        },
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: 3 }}>
        {props.indicatedIssueLabel?.uuid ? 'ラベルの編集' : 'ラベルの追加'}
      </Typography>

      <Stack spacing={2}>
        <Stack spacing={1}>
          <Typography variant="subtitle1">ラベル名</Typography>
          <TextField
            fullWidth
            placeholder="ラベル名"
            value={values.name || ''}
            onChange={e => {
              setFieldValue('name', e.target.value)
            }}
            inputProps={{
              maxLength: 20,
            }}
            error={props.touched.name && Boolean(props.errors.name)}
          />
          <Typography variant="caption" color="textSecondary">
            {values.name?.length || 0} / 20
          </Typography>
          <FormikErrorMessage
            name="name"
            errors={props.errors}
            touched={props.touched}
          />
        </Stack>

        <Stack spacing={1}>
          <Typography variant="subtitle1">色</Typography>
          <Stack direction="row" spacing={1}>
            {[
              colors.error.main,
              colors.warning.main,
              colors.success.main,
              colors.primary.main,
              colors.info.light,
              colors.gray[6],
            ].map(c => {
              const isSelected = values.color?.toUpperCase() === c.toUpperCase()
              return (
                <ButtonBase
                  key={c}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: c,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    border: isSelected ? `2px solid ${c}` : 'none',
                    transition: 'transform 0.2s ease',
                    '&:hover': {
                      transform: 'scale(1.2)',
                    },
                    '&:focus': {
                      outline: `2px solid ${c}`,
                      outlineOffset: '2px',
                    },
                  }}
                  onClick={() => {
                    setFieldValue('color', c)
                  }}
                  type="button"
                  aria-label={`色 ${c} を選択`}
                >
                  {isSelected && (
                    <CheckOutlined sx={{ color: colors.white, fontSize: 20 }} />
                  )}
                </ButtonBase>
              )
            })}
          </Stack>
        </Stack>
      </Stack>

      <Box sx={{ marginTop: 4 }}>
        <FooterButton
          spinning={props.isLoadingIndicatedIssueLabel}
          onCancel={() => props.changeIsShowDrawer(false)}
          onSubmit={() => handleSubmit()}
          isKds
        />
      </Box>
    </Drawer>
  )
}

const EditIndicatedIssueLabelDrawer: FC = () => {
  const stateProps = useStateProps()
  const dispatchProps = useDispatchProps()
  const mergeProps = useMergeProps(stateProps, dispatchProps)
  const formikProps = useFormik<IndicatedIssueLabel>({
    initialValues: {
      id: stateProps.indicatedIssueLabel?.id ?? 0,
      uuid: stateProps.indicatedIssueLabel?.uuid,
      name: stateProps.indicatedIssueLabel?.name || '',
      color: stateProps.indicatedIssueLabel?.color || colors.error.main,
    },
    validationSchema,
    onSubmit: (
      indicatedIssueLabel: IndicatedIssueLabel,
      formikHelpers: FormikHelpers<IndicatedIssueLabel>,
    ) => {
      if (!stateProps.indicatedIssueLabel?.uuid) {
        mergeProps.addIndicatedIssueLabel({
          ...indicatedIssueLabel,
          id: 0,
          uuid: UUID(),
        })
      } else {
        mergeProps.updateIndicatedIssueLabel({
          ...indicatedIssueLabel,
          uuid: stateProps.indicatedIssueLabel.uuid,
        })
      }
      formikHelpers.resetForm()
    },
    enableReinitialize: true,
  })
  const props = {
    ...stateProps,
    ...dispatchProps,
    ...mergeProps,
    ...formikProps,
  }
  return <EditIndicatedIssueLabelDrawerScene {...props} />
}

export default EditIndicatedIssueLabelDrawer
